import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceLine } from "recharts";
import { useRef } from "react";

const MacroCompoundVerticalHistogram = ({
  days,
  target: globalTarget,
  height = 350,
  width = "100%",
  macroName,
  unitName,
  valueUnit = "",
}) => {
  // Colors matching the other charts
  const mainGreen = "#598a56";
  const mainGreenDarker = "#3e6b3b";
  const redColor = "#B86850";
  const referenceLineText = "#362e2d";
  const referenceLineColor = "#2f292537"; //Separator
  const tickColor = "hsl(var(--secondary))";

  // Refs for chart dimensions
  const chartRef = useRef(null);

  // Format date based on whether it's in the current week
  const formatDate = (dateString) => {
    const dateObj = new Date(dateString);
    const offsetDate = dateObj.getTimezoneOffset() * 60000;
    const localDate = new Date(dateObj.getTime() + offsetDate);
    return localDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Prepare data for the chart
  const prepareChartData = () => {
    return days.map((day) => {
      const { date, amount, target: dayTarget } = day;

      // Use day-specific target if available, otherwise use global target
      const effectiveTarget = dayTarget || globalTarget;
      const min = effectiveTarget?.min;
      const max = effectiveTarget?.max;

      // Initialize segment values
      let baseValue = 0;
      let middleValue = 0;
      let topValue = 0;

      // Case 1: No target
      if (!effectiveTarget || (effectiveTarget && !min && !max)) {
        baseValue = amount;
      }
      // Case 2: Only min target
      else if (min && !max) {
        if (amount < min) {
          baseValue = amount;
        } else {
          baseValue = min;
          middleValue = amount - min;
        }
      }
      // Case 3: Only max target
      else if (!min && max) {
        if (amount <= max) {
          baseValue = amount;
        } else {
          baseValue = max;
          topValue = amount - max;
        }
      }
      // Case 4: Both min and max targets
      else if (min && max) {
        if (amount < min) {
          baseValue = amount;
        } else if (amount <= max) {
          baseValue = min;
          middleValue = amount - min;
        } else {
          baseValue = min;
          middleValue = max - min;
          topValue = amount - max;
        }
      }

      return {
        name: formatDate(date),
        macroName: macroName,
        unitName: unitName,
        date,
        amount,
        baseValue,
        middleValue,
        topValue,
        min,
        max,
      };
    });
  };

  const data = prepareChartData();

  const calculateMinReferenceValue = () => {
    let minReferenceValue = null;
    for (let i = 0; i < data.length; i++) {
      if (data[i] && data[i].amount && data[i].amount !== 0) {
        if (minReferenceValue === null || data[i].amount < minReferenceValue) {
          minReferenceValue = data[i].amount;
        }
      }
    }
    return minReferenceValue || 0;
  };

  const calculateAverageReferenceValue = () => {
    let total = 0;
    let count = 0;

    for (let i = 0; i < data.length; i++) {
      if (data[i] && data[i].amount && data[i].amount !== 0) {
        total += data[i].amount;
        count++;
      }
    }

    return count > 0 ? total / count : 0;
  };

  const countReferenceValues = () => {
    let count = 0;
    for (let i = 0; i < data.length; i++) {
      if (data[i] && data[i].amount && data[i].amount !== 0) {
        count++;
      }
    }
    return count;
  };

  // Find the maximum value for the y-axis (including both data and targets)
  // Find the maximum value for the y-axis (including both data and targets)
  const maxValue =
    Math.max(
      ...data.map((d) => d.amount || 0),
      ...data.map((d) => (d.max !== undefined && d.max !== null ? d.max : 0)),
      ...data.map((d) => (d.min !== undefined && d.min !== null ? d.min : 0)),
      1 // Ensure we have a non-zero value
    ) * 1.1; // Add 10% padding

  const maxReferenceValue = Math.max(
    ...data.map((d) => d.amount || 0),
    ...data.map((d) => (d.max !== undefined && d.max !== null ? d.max : 0)),
    ...data.map((d) => (d.min !== undefined && d.min !== null ? d.min : 0))
  );

  const minReferenceValue = calculateMinReferenceValue();
  const averageReferenceValue = calculateAverageReferenceValue();
  const referenceValueCount = countReferenceValues();

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Format target information
      let targetInfo = null;
      if (data.min !== undefined && data.min !== null && data.max !== undefined && data.max !== null) {
        targetInfo = `Target: ${data.min}${valueUnit} - ${data.max}${valueUnit}`;
      } else if (data.min !== undefined && data.min !== null) {
        targetInfo = `Target: Min ${data.min}${valueUnit}`;
      } else if (data.max !== undefined && data.max !== null) {
        targetInfo = `Target: Max ${data.max}${valueUnit}`;
      }

      // Calculate percentage of target if applicable
      let percentageInfo = null;
      if (data.min !== undefined && data.min !== null && data.amount > 0) {
        const percentage = Math.round((data.amount / data.min) * 100);
        percentageInfo = `${percentage}% of min target`;
      } else if (data.max !== undefined && data.max !== null && data.amount > 0) {
        const percentage = Math.round((data.amount / data.max) * 100);
        percentageInfo = `${percentage}% of max target`;
      }

      return (
        <div className="bg-background border border-border rounded shadow-md p-3">
          <p className="font-normal text-primaryText text-sm leading-snug">{data.macroName}</p>
          <p className="font-normal text-mainGreen text-sm mb-2">{data.name}</p>
          <p className="font-medium text-foreground text-primaryText">
            {data.amount}
            <span className="font-normal text-primaryText">&nbsp;{data.unitName}</span>
          </p>

          {targetInfo && <p className="text-muted-foreground mt-1">{targetInfo}</p>}
          {percentageInfo && <p className="text-muted-foreground">{percentageInfo}</p>}
        </div>
      );
    }
    return null;
  };

  // Determine which bar should have rounded corners
  const getBarRadius = (dataKey) => {
    // For the baseValue bar, round the top if it's the only segment
    if (dataKey === "baseValue") {
      return [0, 0, 0, 0];
    }
    // For the middleValue bar, round the top if there's no topValue
    else if (dataKey === "middleValue") {
      return [0, 0, 0, 0];
    }
    // For the topValue bar, always round the top
    else if (dataKey === "topValue") {
      return [4, 4, 0, 0];
    }
    return [0, 0, 0, 0];
  };

  return (
    <div className="w-full h-full relative" ref={chartRef}>
      <ResponsiveContainer width={width} height={height}>
        <BarChart data={data} margin={{ top: 20, right: 0, left: 0, bottom: 20 }} barSize={40}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.5} />
          <XAxis
            dataKey="name"
            axisLine={true}
            tickLine={true}
            tick={{ fontSize: 12, fill: referenceLineText }}
            padding={{ left: 64 }}
          />
          <YAxis hide domain={[0, maxValue]} />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            wrapperStyle={{ outline: "none" }}
            isAnimationActive={true}
          />
          {maxReferenceValue !== 0 && referenceValueCount > 1 && (
            <ReferenceLine
              y={maxReferenceValue}
              stroke={referenceLineColor}
              label={{
                position: "insideTopLeft",
                value: `Max: ${maxReferenceValue} ${unitName}`,
                fill: referenceLineText,
                fontSize: 12,
              }}
              isFront={true}
            />
          )}
          {averageReferenceValue !== 0 && referenceValueCount > 1 && (
            <ReferenceLine
              y={averageReferenceValue}
              stroke={referenceLineColor}
              label={{
                position: "insideTopLeft",
                value: `Avg: ${averageReferenceValue.toFixed(0)} ${unitName}`,
                fill: referenceLineText,
                fontSize: 12,
              }}
              isFront={true}
            />
          )}
          {minReferenceValue !== 0 && referenceValueCount > 1 && (
            <ReferenceLine
              y={minReferenceValue}
              stroke={referenceLineColor}
              label={{
                position: "insideBottomLeft",
                value: `Min: ${minReferenceValue} ${unitName}`,
                fill: referenceLineText,
                fontSize: 12,
              }}
              isFront={true}
            />
          )}
          {referenceValueCount === 1 && (
            <ReferenceLine
              y={maxReferenceValue}
              stroke={referenceLineColor}
              label={{
                position: "insideTopLeft",
                value: `${maxReferenceValue} ${unitName}`,
                fill: referenceLineText,
                fontSize: 12,
              }}
              isFront={true}
            />
          )}
          <Bar dataKey="baseValue" stackId="a" fill={mainGreen} radius={[0, 0, 0, 0]} />
          <Bar dataKey="middleValue" stackId="a" fill={mainGreenDarker} radius={[0, 0, 0, 0]} />
          <Bar dataKey="topValue" stackId="a" fill={redColor} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MacroCompoundVerticalHistogram;
